import React, { useEffect, useState } from "react";
import portalApi from "../../api/portalClient";
import { PortalRunbookDetail, PortalRunbookSummary } from "../../types";
import { formatDate, formatDateTime } from "../../utils/datetime";

export default function PortalRunbooks() {
  const [runbooks, setRunbooks] = useState<PortalRunbookSummary[]>([]);
  const [open, setOpen] = useState<PortalRunbookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/runbooks")
      .then((r) => setRunbooks(r.data))
      .catch(() => setError("Couldn't load runbooks."))
      .finally(() => setLoading(false));
  }, []);

  const openOne = async (id: number) => {
    setError("");
    try {
      const r = await portalApi.get(`/api/v1/portal/runbooks/${id}`);
      setOpen(r.data);
    } catch {
      setError("Couldn't load that runbook. It may have been removed.");
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="max-w-6xl">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Runbooks</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Step-by-step procedures your support team has published for you.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm px-4 py-2.5 rounded-lg">
          {error}
        </div>
      )}

      {runbooks.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-10 text-center text-gray-500 dark:text-gray-400 text-sm">
          No runbooks have been shared with you yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {runbooks.map((rb) => (
            <button
              key={rb.id}
              onClick={() => openOne(rb.id)}
              className="text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-brand/30 transition-all duration-150"
            >
              <div className="font-semibold text-gray-900 dark:text-gray-100 text-base">{rb.title}</div>
              {rb.description && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-3 leading-relaxed">
                  {rb.description}
                </div>
              )}
              <div className="text-[11px] mt-3 flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${
                    rb.customer_id == null
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                      : "bg-brand/10 text-brand dark:text-brand-light"
                  }`}
                >
                  {rb.customer_id == null ? "Shared with all customers" : "Specific to you"}
                </span>
                <span className="text-gray-400 dark:text-gray-500">·</span>
                <span className="text-gray-500 dark:text-gray-400">
                  Updated {formatDate(rb.updated_at)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-brand" />
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{open.title}</h2>
              <button
                onClick={() => setOpen(null)}
                className="text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700/40 transition-colors"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-5">
              {open.description && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-5 leading-relaxed">{open.description}</p>
              )}
              {open.steps.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">No steps in this runbook.</p>
              ) : (
                <ol className="space-y-4">
                  {open.steps.map((s, idx) => (
                    <li key={idx} className="border-l-4 border-brand pl-4 py-1">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Step {idx + 1}: {s.title}
                      </div>
                      {s.body && (
                        <pre className="text-sm text-gray-700 dark:text-gray-300 mt-1.5 whitespace-pre-wrap font-sans leading-relaxed">
                          {s.body}
                        </pre>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                Last updated {formatDateTime(open.updated_at)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
